// /carga-unidades — importador de unidades de postventa (ADR-0144 §2).
//
// Un flujo en una sola pantalla: descargar plantilla → subir → revisar el
// resumen del dry-run (errores por fila) → confirmar → resultado. El
// commit vuelve a mandar el mismo archivo; el servidor lo re-valida y
// aplica todo o nada, así que si entre medio alguien cambió algo, el
// commit puede volver con errores nuevos y se muestran igual que los del
// dry-run.
//
// La pantalla existe sólo si la receta declara `unitProfile` y el rol
// puede importar (canImportUnits); App.tsx redirige si no.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { UnitImportIssue, UnitImportSummary } from "@bitacorapro/api-types";
import { Icon } from "../components/icons";
import { summaryFromRejection, unitImportApi } from "../lib/api/endpoints/unit-import";
import { ApiError } from "../lib/api/error";
import { esFallaIncierta, mensajeDeError } from "../lib/api/error-message";

type State =
  | { kind: "idle" }
  | { kind: "picked"; file: File }
  | { kind: "checking"; file: File }
  | { kind: "reviewed"; file: File; summary: UnitImportSummary }
  | { kind: "committing"; file: File; summary: UnitImportSummary }
  | { kind: "done"; summary: UnitImportSummary }
  | {
      kind: "failed";
      file: File | null;
      message: string;
      summary?: UnitImportSummary;
      /** El commit se cortó sin respuesta legible: pudo aplicarse. */
      uncertain?: boolean;
    };

const ACTION_LABEL: Record<UnitImportSummary["rows"][number]["action"], string> = {
  create: "Nueva",
  update: "Se actualiza",
  unchanged: "Sin cambios",
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** «A», «A y B», «A, B y C». */
export function listaNatural(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? "";
  return `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}`;
}

/** Lo que la confirmación dice antes del commit. El reemplazo de los
 *  contactos de emergencia es destructivo (los vigentes del proyecto se
 *  sustituyen por los de la planilla), así que se nombra siempre que
 *  venga, con los proyectos afectados. */
export function textoConfirmacion(summary: UnitImportSummary): string {
  const { toCreate, toUpdate, emergencyProjects } = summary;
  const proyectos = listaNatural(emergencyProjects);
  const reemplazo =
    emergencyProjects.length === 0
      ? ""
      : `los contactos de emergencia vigentes de ${proyectos} por los de la planilla`;
  if (toCreate + toUpdate === 0) {
    return `Se reemplazarán ${reemplazo}. No se crean ni actualizan unidades. ¿Confirmas?`;
  }
  const unidades =
    `Se ${toCreate === 1 ? "creará" : "crearán"} ${plural(toCreate, "unidad", "unidades")} y se ` +
    `${toUpdate === 1 ? "actualizará" : "actualizarán"} ${toUpdate}.`;
  return reemplazo
    ? `${unidades} También se reemplazarán ${reemplazo}. ¿Confirmas?`
    : `${unidades} ¿Confirmas?`;
}

const COMMIT_INCIERTO =
  "No pudimos confirmar si la carga se guardó: se cortó la conexión o el servidor falló. " +
  "Revisa las unidades de tus proyectos antes de reintentar. Si vuelves a subir la misma " +
  "planilla, no se duplican unidades.";

export function CargarUnidades() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ kind: "idle" });
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const file = "file" in state ? state.file : null;
  const busy = state.kind === "checking" || state.kind === "committing";

  async function downloadTemplate() {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await unitImportApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bitacorapro-carga-unidades.xlsx";
      a.hidden = true;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(mensajeDeError(err, "No pudimos descargar la plantilla. Intenta de nuevo."));
    } finally {
      setDownloading(false);
    }
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    setConfirming(false);
    setState(picked ? { kind: "picked", file: picked } : { kind: "idle" });
  }

  async function check() {
    if (!file) return;
    setConfirming(false);
    setState({ kind: "checking", file });
    try {
      const summary = await unitImportApi.dryRun(file);
      setState({ kind: "reviewed", file, summary });
    } catch (err) {
      setState({
        kind: "failed",
        file,
        // El dry-run no escribe: aun cortado, no se guardó nada.
        message: mensajeDeError(
          err,
          "No pudimos revisar la planilla. No se guardó nada; intenta de nuevo."
        ),
      });
    }
  }

  async function commit() {
    if (state.kind !== "reviewed") return;
    setConfirming(false);
    setState({ kind: "committing", file: state.file, summary: state.summary });
    try {
      const summary = await unitImportApi.commit(state.file);
      setState({ kind: "done", summary });
      // Proyectos, unidades y fichas cambian: todo lo cacheado queda viejo.
      void qc.invalidateQueries();
    } catch (err) {
      const rejected = err instanceof ApiError ? summaryFromRejection(err) : null;
      // Un 4xx es un rechazo (todo o nada: no se aplicó). Un corte de red,
      // un 5xx o una respuesta ilegible no dicen si la transacción alcanzó
      // a terminar en el servidor.
      const uncertain = esFallaIncierta(err);
      if (uncertain) void qc.invalidateQueries();
      setState({
        kind: "failed",
        file: state.file,
        message: uncertain ? COMMIT_INCIERTO : mensajeDeError(err, COMMIT_INCIERTO),
        ...(rejected ? { summary: rejected } : {}),
        ...(uncertain ? { uncertain: true } : {}),
      });
    }
  }

  function reset() {
    if (fileRef.current) fileRef.current.value = "";
    setConfirming(false);
    setState({ kind: "idle" });
  }

  const summary =
    state.kind === "reviewed" || state.kind === "committing"
      ? state.summary
      : state.kind === "failed"
        ? state.summary
        : undefined;
  const pending = summary ? summary.toCreate + summary.toUpdate : 0;
  // Sólo contactos de emergencia: no hay unidades que cargar.
  const soloEmergencias = pending === 0 && (summary?.emergencyProjects.length ?? 0) > 0;
  const canCommit =
    state.kind === "reviewed" &&
    state.summary.errors.length === 0 &&
    (pending > 0 || state.summary.emergencyProjects.length > 0);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">Gestión</div>
          <h1 className="page-header__title">Cargar unidades</h1>
          <p className="page-header__sub">
            Crea o actualiza las unidades de tus proyectos desde una planilla: tipología,
            estacionamientos, bodegas, fechas de recepción DOM, inscripción CBR y entrega, y los
            datos de contacto del propietario. Cargar una unidad no la activa ni invita al
            propietario; eso se hace desde la ficha de cada unidad.
          </p>
        </div>
      </div>

      {state.kind === "done" ? (
        <DonePanel summary={state.summary} onReset={reset} />
      ) : (
        <div className="stack-gap">
          <section className="panel">
            <div className="panel__head">
              <h2 className="panel__title">1 · Descarga la plantilla</h2>
            </div>
            <div className="panel__body unit-import__row">
              <p className="cell-meta">
                Trae las hojas «Unidades» y «Emergencias», una fila de ejemplo y una hoja de
                instrucciones. Los proyectos tienen que existir antes de cargar sus unidades.
              </p>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={downloading}
                onClick={() => void downloadTemplate()}
              >
                <Icon.Download s={13} />
                {downloading ? "Descargando…" : "Descargar plantilla"}
              </button>
              {downloadError && (
                <p className="auth-error" role="alert">
                  {downloadError}
                </p>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel__head">
              <h2 className="panel__title">2 · Sube la planilla y revísala</h2>
            </div>
            <div className="panel__body">
              <div className="field">
                <label className="field__label" htmlFor="unit-import-file">
                  Planilla .xlsx
                </label>
                <input
                  id="unit-import-file"
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={busy}
                  onChange={onPick}
                />
                {file && (
                  <p className="field__hint">
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                  </p>
                )}
              </div>
              <div className="unit-import__row">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!file || busy}
                  onClick={() => void check()}
                >
                  {state.kind === "checking" ? "Revisando…" : "Revisar planilla"}
                </button>
                {state.kind !== "idle" && (
                  <button type="button" className="btn btn--ghost" disabled={busy} onClick={reset}>
                    Empezar de nuevo
                  </button>
                )}
              </div>
              {/* Progreso de una acción (revisar la planilla), no la carga de
                  la pantalla: queda fuera de la norma de skeleton (ADR-0166). */}
              {state.kind === "checking" && (
                <p className="cell-meta" role="status">
                  Revisando la planilla. Todavía no se guarda nada.
                </p>
              )}
              {state.kind === "failed" && (
                <div className="auth-error" role="alert">
                  <p>{state.message}</p>
                  {state.uncertain && (
                    <p>
                      <Link to="/proyectos">Ver proyectos</Link>
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {summary && (
            <section className="panel" aria-labelledby="unit-import-summary">
              <div className="panel__head">
                <h2 className="panel__title" id="unit-import-summary">
                  3 · Revisa y confirma
                </h2>
                <p className="panel__sub">
                  {summary.errors.length > 0
                    ? "Corrige los errores en la planilla y vuelve a subirla. Mientras haya errores no se carga nada."
                    : "Nada se ha guardado todavía."}
                </p>
              </div>
              <div className="panel__body stack-gap">
                <SummaryTiles summary={summary} />
                {summary.errors.length > 0 && (
                  <IssueList
                    title={plural(summary.errors.length, "error", "errores")}
                    issues={summary.errors}
                    tone="error"
                  />
                )}
                {summary.warnings.length > 0 && (
                  <IssueList
                    title={plural(summary.warnings.length, "aviso", "avisos")}
                    issues={summary.warnings}
                    tone="warning"
                  />
                )}
                {summary.rows.length > 0 && <RowsTable summary={summary} />}

                {state.kind === "reviewed" && summary.errors.length === 0 && !canCommit && (
                  <p className="notice-banner">
                    La planilla no trae cambios: todo está igual que en BitácoraPro.
                  </p>
                )}
                {canCommit && !confirming && (
                  <div className="unit-import__row">
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => setConfirming(true)}
                    >
                      {soloEmergencias
                        ? "Actualizar contactos de emergencia"
                        : `Cargar ${plural(pending, "unidad", "unidades")}`}
                    </button>
                  </div>
                )}
                {canCommit && confirming && (
                  <div
                    className="notice-banner unit-import__confirm"
                    role="group"
                    aria-label="Confirmar carga"
                  >
                    <span>{textoConfirmacion(summary)}</span>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => void commit()}
                    >
                      {soloEmergencias ? "Sí, reemplazar contactos" : "Sí, cargar"}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setConfirming(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
                {state.kind === "committing" && (
                  <p className="cell-meta" role="status">
                    {soloEmergencias
                      ? "Actualizando contactos de emergencia…"
                      : // fitness-allow: skeleton-loading-owned-by-primitives — progreso de la acción «Sí, cargar» (subir unidades), no la carga de la pantalla; ADR-0166 deja los estados de acción fuera de la norma
                        "Cargando unidades…"}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryTiles({ summary }: { summary: UnitImportSummary }) {
  return (
    <div className="kpi-grid unit-import__tiles">
      <Tile label="Unidades nuevas" value={summary.toCreate} />
      <Tile label="Se actualizan" value={summary.toUpdate} />
      <Tile label="Sin cambios" value={summary.unchanged} />
      <Tile
        label="Proyectos con contactos de emergencia"
        value={summary.emergencyProjects.length}
      />
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="kpi-tile kpi-tile--dense">
      <div className="kpi-tile__label">{label}</div>
      <div className="kpi-tile__value">{value}</div>
    </div>
  );
}

function IssueList({
  title,
  issues,
  tone,
}: {
  title: string;
  issues: UnitImportIssue[];
  tone: "error" | "warning";
}) {
  return (
    <div
      className={tone === "error" ? "auth-error" : "notice-banner"}
      role={tone === "error" ? "alert" : undefined}
    >
      <p className="unit-import__issues-title">{title}</p>
      <ul className="workbook-issue-list">
        {issues.map((iss, i) => (
          <li key={i} className="workbook-issue-list__item">
            <strong>
              {iss.sheet}
              {iss.row !== null ? ` · fila ${iss.row}` : ""}
            </strong>
            : {iss.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RowsTable({ summary }: { summary: UnitImportSummary }) {
  return (
    <table className="dtable">
      <thead>
        <tr>
          <th className="col-num">Fila</th>
          <th>Proyecto</th>
          <th>Unidad</th>
          <th>Qué pasa</th>
        </tr>
      </thead>
      <tbody>
        {summary.rows.map((r) => (
          <tr key={r.row}>
            <td className="col-num">{r.row}</td>
            <td>{r.project}</td>
            <td className="cell-strong">{r.unit}</td>
            <td>{ACTION_LABEL[r.action]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DonePanel({ summary, onReset }: { summary: UnitImportSummary; onReset: () => void }) {
  return (
    <section className="panel" aria-live="polite">
      <div className="panel__head">
        <h2 className="panel__title">Listo</h2>
      </div>
      <div className="panel__body stack-gap">
        <p>
          {summary.toCreate + summary.toUpdate > 0 &&
            `Se ${summary.toCreate === 1 ? "creó" : "crearon"} ` +
              `${plural(summary.toCreate, "unidad", "unidades")} y se ` +
              `${summary.toUpdate === 1 ? "actualizó" : "actualizaron"} ${summary.toUpdate}.`}
          {summary.toCreate + summary.toUpdate > 0 && summary.emergencyProjects.length > 0 && " "}
          {summary.emergencyProjects.length > 0 &&
            `Se reemplazaron los contactos de emergencia de ${listaNatural(summary.emergencyProjects)}.`}
        </p>
        {summary.toCreate > 0 && (
          <p className="cell-meta">
            Las unidades nuevas quedan sin activar. Para invitar al propietario, entra a la ficha de
            la unidad.
          </p>
        )}
        <div className="unit-import__row">
          <Link to="/proyectos" className="btn btn--primary">
            Ver proyectos
          </Link>
          <button type="button" className="btn btn--secondary" onClick={onReset}>
            Cargar otra planilla
          </button>
        </div>
      </div>
    </section>
  );
}
